  import React, { useState, useEffect, useCallback, useRef } from 'react';
  import { useLocation, useNavigate, Link } from 'react-router-dom';
  import { Star, MapPin, Clock, Briefcase, Search, Filter, ChevronDown, ChevronUp, ArrowRight, SlidersHorizontal } from 'lucide-react';

  import Header from './header';
  import CustomerHeader from '../customer/CustomerHeader';
  import Footer from './Footer';
  import Newsletter from './HomeNewsletter';
  import ServiceMap from './ServiceMap';
  import PriceRangeSlider from './PriceRangeSlider';
  import { API_BASE_URL, BACKEND_BASE_URL, normalizeImageUrl } from '../../utils/api';
  import { useCustomerAuth } from '../../contexts/CustomerAuthContext';

  interface Worker {
    id: number;
    name: string;
    firstName: string;
    lastName: string;
    profilePicture: string;
    bio: string;
    parish: string;
    city?: string;
    state?: string;
    hourlyRate: number;
    rating: number;
    totalReviews: number;
    experienceYears: number;
    verificationStatus: string;
    membershipTier?: string;
    membershipTierDisplay?: string;
    verifiedBadge?: boolean;
    phoneVisible?: boolean;
    phone?: string;
  }

  interface Service {
    id: number;
    title: string;
    description: string;
    price: number;
    duration: string;
    category: string;
    image?: string;
    createdAt: string;
    worker: Worker;
  }

  interface SearchData {
    services: Service[];
    count: number;
    pagination: {
      currentPage: number;
      totalPages: number;
      totalItems: number;
      itemsPerPage: number;
      hasNextPage: boolean;
      hasPrevPage: boolean;
    };
    filters: {
      category?: string;
      parish?: string;
      minPrice?: number;
      maxPrice?: number;
      sort?: string;
    };
  }

  const SearchResults: React.FC = () => {
    const { isAuthenticated } = useCustomerAuth();
    const location = useLocation();
    const navigate = useNavigate();

    // Derive query params first so they can be used as initial state values
    const searchParams = new URLSearchParams(location.search);
    const query = searchParams.get('q') || '';
    const locationQuery = searchParams.get('location') || '';
    const category = searchParams.get('category') || '';
    const page = searchParams.get('page') || '1';
    const minPrice = searchParams.get('minPrice') || '';
    const maxPrice = searchParams.get('maxPrice') || '';

    const [searchData, setSearchData] = useState<SearchData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Price filter state
    const [priceRange, setPriceRange] = useState({ min: 0, max: 1000 });
    const [selectedPriceRange, setSelectedPriceRange] = useState({ min: 0, max: 1000 });
    const [tempPriceRange, setTempPriceRange] = useState({ min: 0, max: 1000 });

    // Mobile detection
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 991);

    // Filter dropdown state
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [experienceFilter, setExperienceFilter] = useState('');
    const [ratingFilter, setRatingFilter] = useState('');
    const [availableNow, setAvailableNow] = useState(false);
    const [verifiedOnly, setVerifiedOnly] = useState(false);
    const [topRated, setTopRated] = useState(false);
    const [quickResponse, setQuickResponse] = useState(false);
    const [sortOrder, setSortOrder] = useState('top_rated');

    // Local hero search bar state (pre-filled from URL)
    const [searchInput, setSearchInput] = useState(query);
    const [locationInput, setLocationInput] = useState(locationQuery);

    const handleHeroSearch = () => {
      const params = new URLSearchParams();
      if (searchInput.trim()) params.set('q', searchInput.trim());
      if (locationInput.trim()) params.set('location', locationInput.trim());
      navigate(`/search?${params.toString()}`);
    };

    // Refs to prevent unnecessary API calls
    const isInitialMount = useRef(true);
    const prevSearchParams = useRef<string>('');

    // Hide-on-scroll for mobile filter bar
    const [filterBarVisible, setFilterBarVisible] = useState(true);
    const lastScrollY = useRef(0);

    // Initialize price ranges from URL params
    useEffect(() => {
      if (minPrice && maxPrice) {
        const min = parseInt(minPrice);
        const max = parseInt(maxPrice);
        setSelectedPriceRange({ min, max });
        setTempPriceRange({ min, max });
      }
    }, [minPrice, maxPrice]);

    // Fetch search results with proper dependency management
    const fetchSearchResults = useCallback(async () => {
      const parish = searchParams.get('parish') || '';
      const city = searchParams.get('city') || '';
      const genericLocation = searchParams.get('location') || '';

      // Create a unique identifier for the current search parameters
      const currentSearchParams = `${query}-${locationQuery}-${parish}-${city}-${genericLocation}-${category}-${page}-${selectedPriceRange.min}-${selectedPriceRange.max}-${experienceFilter}-${ratingFilter}-${availableNow}-${verifiedOnly}-${topRated}-${quickResponse}`;

      // Don't make API call if parameters haven't changed
      if (prevSearchParams.current === currentSearchParams && !isInitialMount.current) {
        return;
      }

      // Update the previous search params
      prevSearchParams.current = currentSearchParams;

      try {
        setLoading(true);
        setError(null);

        const params = new URLSearchParams();
        if (query) params.append('q', query);
        if (parish) params.append('parish', parish);
        else if (city) params.append('city', city);
        else if (genericLocation) params.append('location', genericLocation);
        else if (locationQuery) params.append('location', locationQuery); // Fallback

        if (category) params.append('category', category);
        params.append('page', page);
        params.append('limit', '12');

        // Add price range parameters
        if (searchParams.get('minPrice')) {
          params.append('minPrice', selectedPriceRange.min.toString());
        }
        if (searchParams.get('maxPrice')) {
          params.append('maxPrice', selectedPriceRange.max.toString());
        }

        if (experienceFilter) params.append('experience', experienceFilter);
        if (ratingFilter) params.append('rating', ratingFilter);
        if (availableNow) params.append('availableNow', 'true');
        if (verifiedOnly) params.append('verifiedOnly', 'true');
        if (topRated) params.append('topRated', 'true');
        if (quickResponse) params.append('quickResponse', 'true');

        const apiUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/search/services?${params}`;
        const response = await fetch(apiUrl);
        const data = await response.json();

        if (data.success) {
          setSearchData(data);

          // Calculate price range from all services if not already set
          if (data.services.length > 0 && priceRange.min === 0 && priceRange.max === 1000) {
            const prices = data.services.map((service: Service) => service.price);
            const minP = Math.floor(Math.min(...prices));
            const maxP = Math.ceil(Math.max(...prices));
            setPriceRange({
              min: minP > 0 ? minP : 0,
              max: maxP > minP ? maxP : 1000
            });
          }
        } else {
          setError('Failed to fetch search results');
        }
      } catch (err) {
        console.error('Search error:', err);
        setError('Failed to fetch search results');
      } finally {
        setLoading(false);
        isInitialMount.current = false;
      }
    }, [
      query,
      locationQuery,
      category,
      page,
      selectedPriceRange,
      experienceFilter,
      ratingFilter,
      availableNow,
      verifiedOnly,
      topRated,
      quickResponse,
      searchParams,
      priceRange.min,
      priceRange.max
    ]);

    // Main effect to handle search
    useEffect(() => {
      const handleResize = () => {
        setIsMobile(window.innerWidth <= 991);
      };

      window.addEventListener('resize', handleResize);
      fetchSearchResults();

      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }, [fetchSearchResults]);

    // Body scroll lock when filters are open
    useEffect(() => {
      if (filtersOpen) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = 'unset';
      }
      return () => {
        document.body.style.overflow = 'unset';
      };
    }, [filtersOpen]);

    // Hide mobile filter bar on scroll down, show on scroll up
    useEffect(() => {
      const handleScroll = () => {
        const currentScrollY = window.scrollY;
        if (currentScrollY > lastScrollY.current + 5) {
          setFilterBarVisible(false);
        } else if (currentScrollY < lastScrollY.current - 5) {
          setFilterBarVisible(true);
        }
        lastScrollY.current = currentScrollY;
      };
      window.addEventListener('scroll', handleScroll, { passive: true });
      return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handlePageChange = (newPage: number) => {
      const params = new URLSearchParams(location.search);
      params.set('page', newPage.toString());
      navigate(`${location.pathname}?${params.toString()}`);
      // Scroll to top
      window.scrollTo(0, 0);
    };

    const servicesForMap = searchData?.services || [];

    if (loading && isInitialMount.current) {
      return (
        <div className="min-h-screen">
          <Header />
          <div className="container mx-auto px-4 py-8">
            <div className="flex justify-center items-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Searching for services...</p>
              </div>
            </div>
          </div>
          <Footer />
        </div>
      );
    }

    return (
      <div className="search-results-wrapper">
        <style>{`
          .listing-page {
            background-color: #F8FAFD;
          }
          .left-filter {
            background: #ffffff;
            border-right: 1px solid #E8EFF7;
            padding: 24px;
          }
          .left-filter h6 {
            font-size: 18px;
            font-weight: 700;
            color: #1C2B38;
            margin-bottom: 24px;
            letter-spacing: -0.02em;
            border-bottom: 2px solid #E8EFF7;
            padding-bottom: 12px;
          }
          .left-filter h5 {
            font-size: 14px;
            font-weight: 600;
            color: #1C2B38;
            opacity: 1;
            margin-top: 24px;
            margin-bottom: 14px;
            text-transform: uppercase;
            letter-spacing: 0.05em;
          }
          .left-filter ul.feature-list {
            border-bottom: 1px solid #E8EFF7;
            padding-bottom: 20px;
          }
          .left-filter ul.feature-list li span {
            background: #ffffff;
            border: 1px solid #D2E3F3;
            color: #4A5E70;
            font-weight: 500;
            padding: 8px 16px;
            font-size: 13px;
            border-radius: 30px;
            transition: all 0.2s ease;
          }
          .left-filter ul.feature-list li span:hover {
            background: #E6F1FB;
            border-color: #185FA5;
            color: #185FA5;
            box-shadow: none;
          }
          .left-filter ul.feature-list li span.selected {
            background: #185FA5;
            border-color: #185FA5;
            color: #ffffff;
            box-shadow: 0 4px 12px rgba(24, 95, 165, 0.15);
          }
          .left-filter .form-check-input:checked {
            background-color: #185FA5;
            border-color: #185FA5;
          }
          .left-filter .form-check-label {
            color: #4A5E70;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
          }
          .left-filter ul.raio-list {
            border-bottom: 1px solid #E8EFF7;
            padding-bottom: 20px;
          }
          .sopnser-card {
            background: #ffffff;
            border: 1px solid #E8EFF7;
            border-radius: 16px;
            padding: 20px;
            margin-bottom: 20px;
            box-shadow: 0 4px 20px rgba(24, 95, 165, 0.03);
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            display: flex;
            column-gap: 24px;
            align-items: center;
          }
          .sopnser-card:hover {
            transform: translateY(-2px);
            box-shadow: 0 10px 25px rgba(24, 95, 165, 0.08);
            border-color: #D2E3F3;
          }
          .left-spon-image {
            width: 200px;
            height: 180px;
            border-radius: 12px;
            overflow: hidden;
            background: #F4F9FD;
            flex-shrink: 0;
            align-self: center;
          }
          .left-spon-image img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            border-radius: 12px;
            transition: transform 0.3s ease;
          }
          .sopnser-card:hover .left-spon-image img {
            transform: scale(1.04);
          }
          .right-spon-image {
            padding: 0;
            width: calc(100% - 224px);
            display: flex;
            flex-direction: column;
          }
          .sopn-head h5 {
            font-size: 18px;
            font-weight: 700;
            color: #1C2B38;
            margin-top: 0;
            margin-bottom: 6px;
            transition: color 0.2s ease;
          }
          .sopn-head h5:hover {
            color: #185FA5;
          }
          .sopn-head span {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            font-size: 13px;
            color: #6C7A87;
            font-weight: 500;
          }
          .sopn-head span img {
            width: 14px;
            height: 14px;
            opacity: 0.7;
          }
          .estimate {
            margin-bottom: 6px;
            font-size: 14px;
            color: #6C7A87;
            padding: 2px 0;
          }
          .estimate p {
            margin: 0;
            font-size: 14px;
            color: #6C7A87;
            display: inline-flex;
            align-items: center;
            gap: 6px;
          }
          .estimate p b {
            color: #1C2B38;
            font-weight: 600;
          }
          .estimate p img {
            width: 16px;
            height: 16px;
            opacity: 0.8;
          }
          .provider-row {
            display: inline-flex;
            align-items: center;
            background: #F4F9FD;
            padding: 6px 12px;
            border-radius: 8px;
            margin-top: 4px;
            border: 1px solid #E8EFF7;
            width: fit-content;
          }
          .provider-row p {
            font-size: 13px !important;
            font-weight: 500;
            color: #185FA5 !important;
            display: inline-flex;
            align-items: center;
            gap: 4px;
          }
          .provider-row p b {
            color: #4A5E70;
            font-weight: 600;
          }
          .provider-avatar-icon {
            width: 20px !important;
            height: 20px !important;
            border-radius: 50%;
            border: 1.5px solid #ffffff;
            box-shadow: 0 2px 4px rgba(0,0,0,0.05);
          }
          .right-spon-image a.cm-btn {
            background: linear-gradient(135deg, #185FA5 0%, #0C467E 100%) !important;
            border: none;
            border-radius: 10px;
            color: #ffffff !important;
            font-size: 14px;
            font-weight: 700;
            padding: 10px 24px !important;
            height: auto !important;
            width: fit-content !important;
            float: none !important;
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            margin-top: 14px;
            text-transform: none;
            box-shadow: 0 4px 12px rgba(24, 95, 165, 0.15);
            transition: all 0.2s ease;
          }
          .right-spon-image a.cm-btn:hover {
            transform: translateY(-1px);
            box-shadow: 0 6px 18px rgba(24, 95, 165, 0.25);
            filter: brightness(1.1);
          }
          .right-map {
            border-left: 1px solid #E8EFF7;
            background: #EBF3FA;
            box-shadow: inset 5px 0 15px rgba(24, 95, 165, 0.02);
          }
          .pagination-wrapper .page-link {
            border: 1px solid #E8EFF7;
            color: #185FA5;
            padding: 8px 16px;
            font-size: 14px;
            font-weight: 600;
            border-radius: 8px;
            margin: 0 4px;
            background: #ffffff;
            transition: all 0.2s ease;
          }
          .pagination-wrapper .page-item.active .page-link {
            background: #185FA5;
            border-color: #185FA5;
            color: #ffffff;
          }
          .pagination-wrapper .page-link:hover {
            background: #E6F1FB;
            color: #0C467E;
            border-color: #185FA5;
          }
          .pagination-wrapper .page-item.disabled .page-link {
            background: #F9FAFB;
            color: #9CA3AF;
            border-color: #E5E7EB;
          }
        `}</style>
        {/* Conditionally render header based on authentication */}
        {isAuthenticated ? <CustomerHeader /> : <Header />}

        {/* Browse Services Hero */}
        <section className="premium-services-hero">
          <span className="premium-services-hero-badge">Browse services</span>
          <h1 className="premium-services-hero-title">Find the right professional, first time.</h1>
          <p className="premium-services-hero-subtitle">
            Search across categories, skills, and parishes to shortlist trusted pros in seconds.
          </p>

          {/* Search Pill Card */}
          <div className="premium-search-pill-card" style={{ marginTop: '32px', maxWidth: '768px' }}>
            {/* Service Search Input */}
            <div className="premium-search-field">
              <Search size={20} />
              <input
                type="text"
                placeholder="Search for a service, skill, or provider"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleHeroSearch()}
                autoComplete="off"
              />
            </div>

            {/* Divider */}
            <div className="premium-search-divider"></div>

            {/* Location Search Input */}
            <div className="premium-search-field">
              <MapPin size={20} />
              <input
                type="text"
                placeholder="Parish or city"
                value={locationInput}
                onChange={(e) => setLocationInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleHeroSearch()}
                autoComplete="off"
              />
            </div>

            {/* Search Button */}
            <button
              type="button"
              className="premium-search-btn"
              style={{ minWidth: '120px' }}
              onClick={handleHeroSearch}
            >
              Search
            </button>
          </div>
        </section>

          <div className="premium-listing-page">

          {/* Mobile/Tablet Filter Toggle Button */}
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '0' }}>
            <button
              type="button"
              className="premium-filter-toggle-btn"
              onClick={() => setFiltersOpen(!filtersOpen)}
              style={{
                background: '#185FA5',
                color: '#fff',
                border: 'none',
                borderRadius: '10px',
                padding: '10px 20px',
                fontWeight: 600,
                fontSize: '14px',
                cursor: 'pointer',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '12px',
                width: '100%',
                justifyContent: 'center',
              }}
            >
              <SlidersHorizontal size={16} style={{ marginRight: '6px' }} />
              {filtersOpen ? 'Hide Filters' : 'Show Filters'}
            </button>
          </div>

          {/* Sidebar Filters */}
          <aside className={`premium-filter-sidebar${filtersOpen ? ' sidebar-visible' : ' sidebar-hidden'}`}>
            <div className="premium-filter-header">
              <h2 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <SlidersHorizontal size={18} />
                Filters
              </h2>
              <button
                type="button"
                onClick={() => {
                  setAvailableNow(false);
                  setVerifiedOnly(false);
                  setTopRated(false);
                  setQuickResponse(false);
                  setExperienceFilter('');
                  setRatingFilter('');
                }}
                className="premium-clear-btn"
              >
                Clear all
              </button>
            </div>

            {/* Service Type Filters */}
            <div className="premium-filter-section">
              <h3>Service Type</h3>
              <div className="premium-skills-flex">
                {[{ label: 'Available Now', state: availableNow, toggle: () => setAvailableNow(!availableNow) },
                  { label: 'Verified Only', state: verifiedOnly, toggle: () => setVerifiedOnly(!verifiedOnly) },
                  { label: 'Top Rated', state: topRated, toggle: () => setTopRated(!topRated) },
                  { label: 'Quick Response', state: quickResponse, toggle: () => setQuickResponse(!quickResponse) },
                ].map(({ label, state, toggle }) => (
                  <button
                    key={label}
                    type="button"
                    className={`premium-skill-pill ${state ? 'active' : ''}`}
                    onClick={toggle}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Experience Level */}
            <div className="premium-filter-section">
              <h3>Experience Level</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {[{ label: 'Beginner (0-2 years)', value: 'beginner' },
                  { label: 'Intermediate (3-5 years)', value: 'intermediate' },
                  { label: 'Expert (5+ years)', value: 'expert' },
                ].map(({ label, value }) => (
                  <button
                    key={value}
                    type="button"
                    className={`premium-rating-option ${experienceFilter === value ? 'active' : ''}`}
                    onClick={() => setExperienceFilter(experienceFilter === value ? '' : value)}
                  >
                    {label}
                    {experienceFilter === value && (
                      <span style={{ width: '6px', height: '6px', backgroundColor: '#185FA5', borderRadius: '50%', marginLeft: '8px' }}></span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Rating */}
            <div className="premium-filter-section">
              <h3 style={{ textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.05em', color: '#9CA3AF' }}>Minimum Rating</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {[{ label: 'Any rating', value: '' },
                  { label: '3+ Stars', value: '3' },
                  { label: '4+ Stars', value: '4' },
                  { label: '5 Stars', value: '5' },
                ].map(({ label, value }) => (
                  <button
                    key={value}
                    type="button"
                    className={`premium-rating-option ${ratingFilter === value ? 'active' : ''}`}
                    onClick={() => setRatingFilter(ratingFilter === value ? '' : value)}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      {value && (
                        <span style={{ display: 'inline-flex', gap: '2px' }}>
                          {Array.from({ length: Number(value) }).map((_, i) => (
                            <Star key={i} size={13} fill="#EF9F27" stroke="#EF9F27" />
                          ))}
                        </span>
                      )}
                      {!value && <span>{label}</span>}
                      {value && <span>& up</span>}
                    </span>
                    {ratingFilter === value && (
                      <span style={{ width: '6px', height: '6px', backgroundColor: '#EF9F27', borderRadius: '50%', marginLeft: '8px' }}></span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <main className="premium-listing-main">
            {/* Top Info Bar */}
            <div className="premium-listing-top-bar">
              <div className="premium-listing-count">
                {searchData?.count || 0}
                <span>services found</span>
              </div>
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                style={{ outline: 'none', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '8px 12px', fontSize: '14px', fontWeight: 500, color: '#374151', background: '#fff', cursor: 'pointer' }}
              >
                <option value="top_rated">Top Rated</option>
                <option value="price_asc">Price: Low to High</option>
                <option value="price_desc">Price: High to Low</option>
                <option value="most_reviews">Most Reviews</option>
                <option value="newest">Newest</option>
              </select>
            </div>

            {error && <div className="alert alert-danger mb-4">{error}</div>}

            {loading && (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Searching...</span>
                </div>
              </div>
            )}

            {!loading && searchData && searchData.services.length === 0 && (
              <div style={{ background: '#fff', border: '1px solid #F3F4F6', borderRadius: '16px', padding: '48px', textAlign: 'center' }}>
                <h5 style={{ color: '#6B7280', fontWeight: 600 }}>No services found</h5>
                <p style={{ color: '#9CA3AF', fontSize: '14px' }}>Try adjusting your search criteria or location</p>
                <button onClick={() => navigate('/')} style={{ background: '#185FA5', color: '#fff', border: 'none', borderRadius: '10px', padding: '10px 24px', fontWeight: 700, marginTop: '16px', cursor: 'pointer' }}>Back to Home</button>
              </div>
            )}

            {!loading && searchData && searchData.services.length > 0 && (() => {
              const sorted = [...searchData.services].sort((a, b) => {
                if (sortOrder === 'price_asc') return a.price - b.price;
                if (sortOrder === 'price_desc') return b.price - a.price;
                if (sortOrder === 'most_reviews') return (b.worker.totalReviews || 0) - (a.worker.totalReviews || 0);
                if (sortOrder === 'newest') return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
                // top_rated (default)
                return (b.worker.rating || 0) - (a.worker.rating || 0);
              });
              return (
              <>
                <div className="premium-services-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
                  {sorted.map((service) => (
                    <div
                      key={service.id}
                      className="premium-service-card"
                      style={{ padding: '20px', minHeight: '380px' }}
                    >
                      <div>
                        {/* Image / Fallback */}
                        <div style={{ position: 'relative' }}>
                          {(service.image || service.worker.profilePicture) ? (
                            <div className="premium-card-image-wrapper" style={{ marginTop: 0 }}>
                              <img
                                src={normalizeImageUrl(
                                  service.image || service.worker.profilePicture,
                                  '/assets/images/sp-one.png'
                                )}
                                alt={service.title}
                                onError={(e) => {
                                  (e.target as HTMLImageElement).src = '/assets/images/sp-one.png';
                                }}
                              />
                            </div>
                          ) : (
                            <div className="premium-card-icon-wrapper" style={{ marginTop: 0 }}>
                              <Briefcase size={36} strokeWidth={1.5} />
                            </div>
                          )}
                          {/* Category Badge */}
                          <span
                            className="premium-card-tag"
                            style={{ position: 'absolute', top: '12px', left: '12px', zIndex: 2, boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}
                          >
                            {service.category}
                          </span>
                        </div>

                        {/* Title & Rating */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginTop: '16px', gap: '12px' }}>
                          <Link to={`/detail/${service.id}`} style={{ textDecoration: 'none', flex: 1 }}>
                            <h4 className="premium-service-card-title" style={{ marginTop: 0, marginBottom: 0 }}>
                              {service.title}
                            </h4>
                          </Link>
                          <div className="premium-rating-badge">
                            <Star size={12} fill="#D97706" stroke="#D97706" />
                            <span>{service.worker.rating ? Number(service.worker.rating).toFixed(1) : '0.0'}</span>
                          </div>
                        </div>

                        {/* Provider */}
                        <span className="premium-service-author">
                          by {service.worker.name || `${service.worker.firstName} ${service.worker.lastName}`}
                        </span>

                        {/* Description */}
                        <p className="premium-service-desc">
                          {service.description ? `${service.description.substring(0, 100)}${service.description.length > 100 ? '...' : ''}` : 'Professional service provider.'}
                        </p>

                        {/* Info Badges */}
                        <div className="premium-service-badges">
                          <span className="premium-badge" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            <MapPin size={10} />
                            {service.worker.city && service.worker.state
                              ? `${service.worker.city}, ${service.worker.state}`
                              : service.worker.parish || 'Jamaica'}
                          </span>
                          <span className="premium-badge" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            <Clock size={10} />
                            {service.duration || 'Flexible'}
                          </span>
                          <span className="premium-badge" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            <Briefcase size={10} />
                            {service.worker.experienceYears || '0'} yrs
                          </span>
                        </div>
                      </div>

                      {/* Card Footer */}
                      <div className="premium-service-footer">
                        <div>
                          <span className="premium-price-label">Starting at</span>
                          <span className="premium-price-val">${service.price}</span>
                        </div>
                        <Link to={`/detail/${service.id}`} className="premium-view-btn" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                          View <ArrowRight size={14} />
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {searchData.pagination.totalPages > 1 && (
                  <div className="pagination-wrapper mt-4">
                    <nav aria-label="Search pagination">
                      <ul className="pagination justify-content-center">
                        <li className={`page-item ${!searchData.pagination.hasPrevPage ? 'disabled' : ''}`}>
                          <button className="page-link" onClick={() => handlePageChange(searchData.pagination.currentPage - 1)} disabled={!searchData.pagination.hasPrevPage}>Previous</button>
                        </li>
                        {Array.from({ length: searchData.pagination.totalPages }, (_, i) => i + 1).map((pageNum) => (
                          <li key={pageNum} className={`page-item ${pageNum === searchData.pagination.currentPage ? 'active' : ''}`}>
                            <button className="page-link" onClick={() => handlePageChange(pageNum)}>{pageNum}</button>
                          </li>
                        ))}
                        <li className={`page-item ${!searchData.pagination.hasNextPage ? 'disabled' : ''}`}>
                          <button className="page-link" onClick={() => handlePageChange(searchData.pagination.currentPage + 1)} disabled={!searchData.pagination.hasNextPage}>Next</button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                )}
              </>
              );
            })()}
          </main>
        </div>
        <Newsletter />
        <Footer />
      </div>
    );
  };

  export default SearchResults;